import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type { TemplateVersion } from '../../types/templateModel'
import { TemplateParentLink } from '../templateParentLink/TemplateParentLink'
import { TemplateReferenceChangeModal } from '../updateTemplateReference/TemplateReferenceChangeModal'
import { useTemplateReplacesOptions } from './useTemplateReplacesOptions'

type Props = {
  isEditable?: boolean
}

export const TemplateVersionReplaces = ({ isEditable }: Props) => {
  const translate = useTranslate()
  const { templateVersion } = useTemplateContext()
  const [isChangeReplacesModalOpen, setIsChangeReplacesModalOpen] =
    useState(false)
  const options = useTemplateReplacesOptions(templateVersion)
  const value = useMemo(
    () => options.find((option) => option === templateVersion?.replaces),
    [options, templateVersion?.replaces],
  )

  assertNotEmpty(templateVersion)

  const handleChangeReplaces = async (nextOption: Optional<string>) => {
    const nextTemplateVersion = {
      ...templateVersion,
      replaces: nextOption,
    } satisfies TemplateVersion
    await templateVersion.updateTemplate(nextTemplateVersion)
  }

  return (
    <div className="grid max-w-md grid-cols-2 items-center gap-x-2">
      {templateVersion.replaces ? (
        <TemplateParentLink templateId={templateVersion.replaces} />
      ) : (
        '-'
      )}
      {isEditable && (
        <>
          <Button onClick={() => setIsChangeReplacesModalOpen(true)}>
            {translate(
              'templateViewer.templateVersionDetails.changeReplacesButton.label',
            )}
          </Button>
          <TemplateReferenceChangeModal
            title={translate(
              'templateViewer.templateVersionDetails.changeReplacesModal.title',
            )}
            options={options}
            value={value}
            onChange={handleChangeReplaces}
            isOpen={isChangeReplacesModalOpen}
            onClose={() => setIsChangeReplacesModalOpen(false)}
          />
        </>
      )}
    </div>
  )
}
